import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "fileInput", "sourceType", "startButton", "stopButton", "status", "modelPicker", "modelInput", "dropzone", "fileName"]

  connect() {
    this.mediaRecorder = null
    this.chunks = []
    this.handleDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.handleDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.handleDocumentClick)
  }

  async start() {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      this.setStatus("이 브라우저에서는 녹음을 지원하지 않습니다.")
      return
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      this.chunks = []
      this.mediaRecorder = new MediaRecorder(this.stream)
      this.mediaRecorder.addEventListener("dataavailable", (event) => {
        if (event.data && event.data.size > 0) this.chunks.push(event.data)
      })
      this.mediaRecorder.addEventListener("stop", () => this.attachRecording())
      this.mediaRecorder.start()
      this.startButtonTarget.disabled = true
      this.stopButtonTarget.disabled = false
      this.setStatus("녹음 중입니다. 종료하면 업로드 파일로 자동 첨부됩니다.")
    } catch (error) {
      this.setStatus(`마이크를 시작할 수 없습니다: ${error.message}`)
    }
  }

  stop() {
    if (!this.mediaRecorder || this.mediaRecorder.state === "inactive") return

    this.mediaRecorder.stop()
    this.stream?.getTracks()?.forEach((track) => track.stop())
    this.startButtonTarget.disabled = false
    this.stopButtonTarget.disabled = true
  }

  attachRecording() {
    const mimeType = this.chunks[0]?.type || "audio/webm"
    const blob = new Blob(this.chunks, { type: mimeType })
    if (!blob.size) {
      this.setStatus("녹음된 오디오가 없습니다.")
      return
    }

    const extension = mimeType.includes("mp4") ? "m4a" : "webm"
    const file = new File([blob], `browser-recording-${Date.now()}.${extension}`, { type: mimeType })
    const transfer = new DataTransfer()
    transfer.items.add(file)
    this.fileInputTarget.files = transfer.files
    this.sourceTypeTarget.value = "browser_recording"
    this.updateSelectedFileName(file)
    this.setStatus("녹음 파일이 첨부됐습니다. 제목을 확인한 뒤 생성 버튼을 눌러주세요.")
  }

  openFilePicker() {
    this.fileInputTarget.click()
  }

  handleFileInputChange() {
    const file = this.fileInputTarget.files?.[0]
    this.sourceTypeTarget.value = "upload"
    this.updateSelectedFileName(file)
  }

  dragOverFile(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "copy"
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.add("is-dragover")
  }

  dragLeaveFile(event) {
    if (event.currentTarget.contains(event.relatedTarget)) return
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.remove("is-dragover")
  }

  dropFile(event) {
    event.preventDefault()
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.remove("is-dragover")

    const file = event.dataTransfer?.files?.[0]
    if (!file) return

    const transfer = new DataTransfer()
    transfer.items.add(file)
    this.fileInputTarget.files = transfer.files
    this.sourceTypeTarget.value = "upload"
    this.updateSelectedFileName(file)
  }

  updateSelectedFileName(file) {
    if (!this.hasFileNameTarget) return

    this.fileNameTarget.textContent = file?.name || "파일을 드래그하거나 클릭해서 선택"
  }

  setStatus(message) {
    if (this.hasStatusTarget) this.statusTarget.textContent = message
  }

  toggleModelMenu(event) {
    event.stopPropagation()

    const picker = event.currentTarget.closest(".mn-model-picker")
    if (!picker) return

    const shouldOpen = !picker.classList.contains("is-open")
    this.closeModelPickers(picker)
    picker.classList.toggle("is-open", shouldOpen)
    event.currentTarget.setAttribute("aria-expanded", shouldOpen ? "true" : "false")
  }

  selectModel(event) {
    event.stopPropagation()

    const option = event.currentTarget
    const picker = option.closest(".mn-model-picker")
    const modelName = option.dataset.modelName
    if (!picker || !modelName) return

    const input = picker.parentElement?.querySelector("[data-meeting-note-recorder-target='modelInput']")
    const selected = picker.querySelector(".mn-model-picker__selected")
    const badges = picker.querySelector(".mn-model-picker__button > .mn-badges")
    const optionName = option.querySelector(".mn-model-name")
    const optionBadges = option.querySelector(".mn-badges")

    if (input) input.value = modelName
    if (selected && optionName) selected.innerHTML = optionName.outerHTML
    if (badges && optionBadges) badges.innerHTML = optionBadges.innerHTML

    picker.querySelectorAll(".mn-model-option").forEach((item) => {
      const isSelected = item === option
      item.classList.toggle("is-selected", isSelected)
      item.setAttribute("aria-selected", isSelected ? "true" : "false")
    })
    this.closeModelPickers()
  }

  handleDocumentClick(event) {
    if (this.element.contains(event.target) && event.target.closest(".mn-model-picker")) return

    this.closeModelPickers()
  }

  closeModelPickers(except = null) {
    this.modelPickerTargets.forEach((picker) => {
      if (picker === except) return

      picker.classList.remove("is-open")
      picker.querySelector(".mn-model-picker__button")?.setAttribute("aria-expanded", "false")
    })
  }
}
